import React from 'react';

/**
 * PrivacyPolicyPage component - Platform privacy policy information
 */
const PrivacyPolicyPage = () => {
  const lastUpdated = 'May 1, 2023';
  
  return (
    <div className="bg-gray-50">
      {/* Header section */}
      <section className="bg-primary-700 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg">
            Last Updated: {lastUpdated}
          </p>
        </div>
      </section>
      
      {/* Policy content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none mx-auto">
            <div className="bg-primary-50 border-l-4 border-primary-500 p-4 mb-8 rounded-r">
              <p className="text-lg font-medium text-primary-800 mb-0">
                At Biotech01, we take your privacy seriously. This Privacy Policy describes how we collect, use, and share information about you when you use our website, mobile application, and services (collectively, the "Services").
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Information We Collect</h2>
              
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Information You Provide to Us</h3>
                <p className="mb-3">
                  We collect information you provide directly to us when you:
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2">
                  <li>Create an account or profile</li>
                  <li>Complete forms on our platform</li>
                  <li>Upload documents or data related to your biotech or health tech products</li>
                  <li>Communicate with other users</li>
                  <li>Contact our support team</li>
                  <li>Subscribe to newsletters or updates</li>
                </ul>
                
                <p>
                  This information may include your name, email address, password, company information, professional background, product details, financial projections, and any other information you choose to provide.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Information We Collect Automatically</h3>
                <p className="mb-3">
                  When you use our Services, we automatically collect certain information, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Log information (such as IP address, browser type, pages viewed)</li>
                  <li>Device information (such as device ID, operating system)</li>
                  <li>Usage information (such as features used, actions taken)</li>
                  <li>Location information (with your permission)</li>
                </ul>
              </div>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">How We Use Your Information</h2>
              <p className="mb-3">
                We use the information we collect to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, maintain, and improve our Services</li>
                <li>Create and maintain your account</li>
                <li>Process transactions and send related information</li>
                <li>Connect startups with investors and service providers (with your permission)</li>
                <li>Generate analytics and insights about the biotech and health tech sectors</li>
                <li>Send technical notices, updates, security alerts, and administrative messages</li>
                <li>Respond to your comments, questions, and requests</li>
                <li>Communicate with you about products, services, offers, and events</li>
                <li>Monitor and analyze trends, usage, and activities in connection with our Services</li>
                <li>Detect, investigate, and prevent fraudulent transactions and other illegal activities</li>
                <li>Personalize and improve the Services</li>
              </ul>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">How We Share Your Information</h2>
              <p className="mb-3">
                We may share information about you as follows:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li className="mb-2">
                  <span className="font-semibold">With Other Users:</span> Information you provide in your public profile or when you use our connection features may be shared with other users according to your settings and preferences.
                </li>
                <li className="mb-2">
                  <span className="font-semibold">With Service Providers:</span> We may share information with vendors, consultants, and other service providers who need access to such information to carry out work on our behalf.
                </li>
                <li className="mb-2">
                  <span className="font-semibold">For Legal Reasons:</span> We may disclose information if we believe that disclosure is necessary to comply with any applicable law, regulation, legal process, or governmental request.
                </li>
                <li>
                  <span className="font-semibold">With Your Consent:</span> We may share information with your consent or at your direction.
                </li>
              </ul>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Data Retention</h2>
              <p>
                We retain personal information for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements, or to provide our services.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Your Rights and Choices</h2>
              <p className="mb-3">
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Accessing, correcting, or deleting your personal information</li>
                <li>Withdrawing your consent</li>
                <li>Objecting to our use of your information</li>
                <li>Requesting restriction of processing</li>
                <li>Data portability</li>
              </ul>
              <p className="bg-gray-50 p-4 rounded border border-gray-200">
                To exercise these rights, please contact us at <a href="mailto:privacy@biotech01.com" className="text-primary-600 hover:text-primary-800 font-medium">privacy@biotech01.com</a>.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Data Security</h2>
              <p>
                We take reasonable measures to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction. However, no security system is impenetrable, and we cannot guarantee the security of our systems.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">International Data Transfers</h2>
              <p>
                We may transfer the information we collect about you to countries other than the country in which you initially provided the information. These countries may not have the same data protection laws as your country. By using our Services, you consent to the transfer of your information to these countries.
              </p>
            </div>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Changes to This Policy</h2>
              <p>
                We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
              <p className="mb-2">
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <a href="mailto:privacy@biotech01.com" className="text-primary-600 hover:text-primary-800 font-medium">privacy@biotech01.com</a>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage; 